import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Plus, Users, Eye, MessageSquare, CloudRain, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useSchedule, addDays, iso, startOfWeek, BRAND_COLOUR } from '@/components/schedule/useSchedule';
import { CalendarGrid } from '@/components/schedule/CalendarGrid';
import { BlockModal } from '@/components/schedule/BlockModal';
import { CrewManager } from '@/components/schedule/CrewManager';
import { DaySheetModal } from '@/components/schedule/DaySheetModal';
import { CustomerTextModal } from '@/components/schedule/CustomerTextModal';
import { UpgradeModal, ProBadge } from '@/components/billing/UpgradeModal';
import { useMyPlan, isUpgradeError } from '@/hooks/useMyPlan';

export default function ProjectCalendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { user, isStaff } = useAuth();
  const section = ['probound', 'proepoxy', 'prodecor'].includes(location.pathname.split('/')[1]) ? location.pathname.split('/')[1] : null;
  const installerParam = params.get('installer');
  const installerId = installerParam && installerParam !== user?.user_id ? installerParam : undefined;

  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [crewFilter, setCrewFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [crewOpen, setCrewOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [textBlock, setTextBlock] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const { has } = useMyPlan();
  const textCustomer = Object.assign((id) => setTextBlock(id), { allowed: has('customer_sms') });

  const range = useMemo(() => {
    if (view === 'week') { const s = startOfWeek(anchor); return { from: iso(addDays(s, -7)), to: iso(addDays(s, 13)) }; }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    return { from: iso(addDays(startOfWeek(first), -7)), to: iso(addDays(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0), 14)) };
  }, [view, anchor]);

  const s = useSchedule({ ...range, installerId });
  const readOnly = s.read_only;
  const blocks = crewFilter === 'all' ? s.blocks : s.blocks.filter((b) => (b.crew_ids || []).includes(crewFilter));
  const [unschedFilter, setUnschedFilter] = useState('all');
  const UNSCHED_STATUSES = [['all', 'All'], ['upcoming', 'Upcoming'], ['in_progress', 'In progress'], ['on_hold', 'On hold'], ['completed', 'Completed']];
  const unscheduledAll = s.projects.filter((p) => p.status !== 'cancelled' && (p.unscheduled_stages || []).length > 0);
  const unscheduled = unschedFilter === 'all' ? unscheduledAll : unscheduledAll.filter((p) => p.status === unschedFilter);

  const step = (n) => setAnchor((a) => (view === 'week' ? addDays(a, 7 * n) : new Date(a.getFullYear(), a.getMonth() + n, 1)));
  const title = view === 'week'
    ? `${startOfWeek(anchor).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${addDays(startOfWeek(anchor), 6).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <header className="bg-white border-b-4" style={{ borderColor: BRAND_COLOUR[section] || '#006400' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(installerId ? `/staff/installers/${installerId}` : section ? `/${section}/projects` : '/')} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Back" data-testid="calendar-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-[#27292E] flex items-center gap-2"><CalendarDays className="w-6 h-6 text-[#006400]" /> Project Calendar</h1>
            <p className="text-xs text-gray-500 hidden sm:block">Plan your weeks — allocate parts of each install to days, manage several jobs at once.{Object.keys(s.weather).length > 0 ? ' Weather by Open-Meteo.' : ''}</p>
          </div>
          {readOnly ? (
            <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full flex items-center gap-1" data-testid="calendar-readonly"><Eye className="w-3.5 h-3.5" /> Staff view · read-only</span>
          ) : (
            <>
              {s.crew.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => (has('crew_day_sheet') ? setSheetOpen(true) : setUpgrade('Texting your crew their day sheet'))} data-testid="calendar-daysheet-btn"><MessageSquare className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Send day sheet</span>{!has('crew_day_sheet') && <ProBadge className="ml-1.5" />}</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setCrewOpen(true)} data-testid="calendar-crew-btn"><Users className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Crew{s.crew.length ? ` (${s.crew.length})` : ''}</span></Button>
              <Button size="sm" className="bg-[#006400] hover:bg-[#004d00] text-white" onClick={() => setModal({ start_date: iso(new Date()) })} data-testid="calendar-add-btn"><Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Add block</span></Button>
            </>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex bg-white border rounded-lg p-0.5" data-testid="calendar-view-toggle">
            {['week', 'month'].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`px-3 py-1.5 text-sm font-semibold rounded-md capitalize ${view === v ? 'bg-[#27292E] text-white' : 'text-gray-600'}`}>{v}</button>
            ))}
          </div>
          <div className="inline-flex items-center bg-white border rounded-lg">
            <button onClick={() => step(-1)} className="p-2 hover:bg-gray-50 rounded-l-lg" aria-label="Previous" data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-3 text-sm font-bold text-gray-900 min-w-[190px] text-center" data-testid="calendar-title">{title}</span>
            <button onClick={() => step(1)} className="p-2 hover:bg-gray-50 rounded-r-lg" aria-label="Next" data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="calendar-today">Today</Button>
          {s.crew.length > 0 && (
            <select value={crewFilter} onChange={(e) => setCrewFilter(e.target.value)} className="ml-auto h-9 border rounded-lg px-2 text-sm bg-white" data-testid="calendar-crew-filter">
              <option value="all">Everyone</option>
              {s.crew.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>

        {s.loading ? <div className="text-center py-16 text-gray-500">Loading…</div> : (
          <CalendarGrid view={view} anchor={anchor} blocks={blocks} projects={s.projects} crew={s.crew} marketingCalls={s.marketing_calls} isStaff={isStaff && !installerId} readOnly={readOnly} weather={s.weather} rainRisks={s.rainRisks}
            onAdd={(d) => setModal({ start_date: d })} onEdit={(b) => setModal(b)} onShift={s.shiftBlock} onDuplicate={s.duplicateBlock} />
        )}

        {s.rainRisks.length > 0 && (
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-3" data-testid="rain-risk-panel">
            <div className="flex items-center gap-2 mb-2">
              <CloudRain className="w-4 h-4 text-sky-700" />
              <div className="text-xs font-bold uppercase tracking-wider text-sky-900">Rain risk — resin work on wet days</div>
            </div>
            <div className="space-y-1.5">
              {s.rainRisks.map((r) => {
                const p = s.projects.find((x) => x.id === r.project_id);
                const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                return (
                  <div key={r.block_id} className="flex flex-wrap items-center gap-2 bg-white border border-sky-100 rounded-lg px-3 py-2" data-testid={`rain-risk-${r.block_id}`}>
                    <span className="text-xs font-bold text-sky-800 bg-sky-100 rounded px-1.5 py-0.5">{r.rain_prob}% rain</span>
                    <span className="text-sm font-semibold text-gray-900">{r.label}</span>
                    <span className="text-sm text-gray-600">· {p?.customer_name || 'project'} · {fmt(r.start_date)}{r.end_date !== r.start_date ? ` → ${fmt(r.end_date)}` : ''}</span>
                    {!readOnly && (r.next_dry_date ? (
                      <Button size="sm" className="ml-auto h-8 bg-[#006400] hover:bg-[#004d00] text-white" onClick={() => s.shiftBlock(r.block_id, r.shift_days)} data-testid={`rain-risk-move-${r.block_id}`}>
                        Move to {fmt(r.next_dry_date)} ({r.next_dry_rain_prob ?? 0}%) <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    ) : (
                      <span className="ml-auto text-xs text-gray-500 italic">No dry day in the 7-day forecast</span>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!readOnly && unscheduledAll.length > 0 && (
          <div className="bg-white border rounded-xl p-3" data-testid="unscheduled-panel">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Not yet on the calendar <span className="text-gray-400 font-semibold normal-case tracking-normal" data-testid="unscheduled-count">({unscheduled.length})</span></div>
              <div className="ml-auto inline-flex flex-wrap gap-1" data-testid="unscheduled-status-filter">
                {UNSCHED_STATUSES.map(([key, label]) => {
                  const n = key === 'all' ? unscheduledAll.length : unscheduledAll.filter((p) => p.status === key).length;
                  if (key !== 'all' && n === 0) return null;
                  return (
                    <button key={key} type="button" onClick={() => setUnschedFilter(key)} data-testid={`unscheduled-filter-${key}`}
                      className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${unschedFilter === key ? 'bg-[#27292E] text-white border-[#27292E]' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      {label} {n}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto pr-1" data-testid="unscheduled-list">
              {unscheduled.length === 0 && <div className="text-xs text-gray-400 italic py-1">No projects with this status are waiting to be scheduled.</div>}
              {unscheduled.map((p) => (
                <button key={p.id} type="button" onClick={() => setModal({ start_date: p.install_date || iso(new Date()), project_id: p.id })} data-testid={`unscheduled-${p.id}`}
                  className="text-xs px-2.5 py-1.5 rounded-full border bg-white hover:bg-gray-50 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: BRAND_COLOUR[p.service_type] || '#006400' }} />
                  <span className="font-semibold">{p.customer_name}</span>
                  {p.status !== 'upcoming' && p.status !== 'in_progress' && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 rounded px-1">{p.status.replace('_', ' ')}</span>}
                  <span className="text-gray-500">{p.unscheduled_stages.length} stage{p.unscheduled_stages.length === 1 ? '' : 's'} unscheduled</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      <BlockModal open={!!modal} initial={modal} projects={s.projects} crew={s.crew}
        onSave={(body) => (modal?.id ? s.updateBlock(modal.id, body) : s.createBlock(body))} onDelete={s.deleteBlock} onClose={() => setModal(null)}
        onTextCustomer={readOnly ? undefined : textCustomer} />
      <DaySheetModal open={sheetOpen} onPreview={s.previewDaySheet} onSend={async (b) => { try { return await s.sendDaySheet(b); } catch (e) { if (isUpgradeError(e)) { setSheetOpen(false); setUpgrade('Texting your crew their day sheet'); } throw e; } }} onClose={() => setSheetOpen(false)} />
      <CustomerTextModal blockId={textBlock} onClose={() => setTextBlock(null)} onSent={s.reload} onUpgrade={() => { setTextBlock(null); setUpgrade('Texting customers their install dates'); }} />
      <UpgradeModal open={!!upgrade} feature={upgrade} onClose={() => setUpgrade(null)} />
      <CrewManager open={crewOpen} crew={s.crew} onAdd={s.addCrew} onUpdate={s.updateCrew} onDelete={s.deleteCrew} onClose={() => setCrewOpen(false)} />
    </div>
  );
}
